import { useState, useMemo, useRef, useEffect } from 'react'
import MiddleEllipsis from '../../components/MiddleEllipsis'
import type { DriveFileItem } from '../../lib/api/google-drive-autojobs'

type ProcessingMode = 'single' | 'mbl_hbl' | 'bundled' | 'manifest' | 'invoices'

interface DriveFileWithRole extends DriveFileItem {
  role: 'mbl' | 'hbl' | 'main' | 'sub' | 'other'
}

interface DriveJobGroup {
  id: string
  files: DriveFileWithRole[]
}

interface Props {
  files: DriveFileItem[]
  jobGroups?: DriveJobGroup[]
  mode: ProcessingMode | null
  showAllTypes: boolean
  folderName?: string
  isParsing?: boolean
  parseProgress?: string
  onToggleShowAll: () => void
  onSetRole?: (groupId: string, fileId: string, role: DriveFileWithRole['role']) => void
  onRemoveFile?: (groupId: string, fileId: string) => void
  onParseSelected?: (fileIds: Set<string>, fileRoles: Record<string, DriveFileWithRole['role']>) => void
}

const ROLE_OPTIONS: Array<{ value: DriveFileWithRole['role']; label: string }> = [
  { value: 'mbl', label: 'MBL' },
  { value: 'hbl', label: 'HBL' },
  { value: 'main', label: 'Main' },
  { value: 'sub', label: 'Sub' },
  { value: 'other', label: 'Other' }
]

function getExtensionFilter(mode: ProcessingMode | null): string[] {
  const filters: Record<ProcessingMode, string[]> = {
    single: ['.pdf'],
    mbl_hbl: ['.pdf'],
    bundled: ['.pdf', '.xlsx', '.xls'],
    manifest: ['.pdf', '.xlsx', '.xls'],
    invoices: ['.pdf', '.xlsx', '.xls']
  }
  return mode ? filters[mode] : []
}

function getFileExtension(filename: string): string {
  const match = filename.match(/\.[^.]+$/)
  if (!match) return ''
  const ext = match[0].toLowerCase()
  // Normalize extensions (e.g., .xls and .XLS both become .xls)
  return ext.replace(/^\./, '.').toLowerCase()
}

function formatFileSize(bytes: string): string {
  const size = parseInt(bytes, 10)
  if (isNaN(size)) return '—'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(isoString: string): string {
  try {
    const date = new Date(isoString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
  } catch {
    return '—'
  }
}

function autoDetectFileRole(filename: string, mode?: ProcessingMode | null): DriveFileWithRole['role'] {
  const lowerName = filename.toLowerCase()

  // Check for explicit MBL-related keywords (master bill of lading)
  if (lowerName.includes('mbl') && !lowerName.includes('hbl')) {
    return 'mbl'
  }
  if (lowerName.includes('mawb') || lowerName.includes('arrival notice') || lowerName.includes('arrival n')) {
    return 'mbl'
  }

  // Check for MBL Chinese keywords
  if (filename.includes('主单') || filename.includes('总单')) {
    return 'mbl'
  }

  // Check for HBL Chinese keywords (必须在invoice检查之前，因为分单是HBL)
  if (filename.includes('分单') || filename.includes('分提单') || filename.includes('副本')) {
    return 'hbl'
  }

  // Check for manifest
  if (lowerName.includes('manifest')) {
    return 'main'
  }

  // Check for invoice (不包括分单)
  if (lowerName.includes('invoice')) {
    return 'main'
  }

  // Check for HBLs/bundle/multi-HBL patterns
  if (lowerName.includes('hbl') || lowerName.includes('bundle') || lowerName.includes('汇总') || lowerName.includes('house bill')) {
    return 'hbl'
  }

  // If filename has shipping-related terms, assume HBL
  if (lowerName.includes('提单') || lowerName.includes('bl') || lowerName.includes('waybill')) {
    return 'hbl'
  }

  // For invoices mode: Excel files without explicit keywords default to 'main' (Invoice)
  // For other modes: default to 'hbl'
  if (mode === 'invoices') {
    const ext = getFileExtension(filename)
    if (ext === '.xlsx' || ext === '.xls') {
      return 'main'  // Default to Invoice for Excel files in invoices mode
    }
  }

  // Default: assume other files are HBLs
  return 'hbl'
}

function normalizeRole(role: DriveFileWithRole['role'], mode: ProcessingMode | null): DriveFileWithRole['role'] {
  const options = getModeRoleOptions(mode)
  return options.some(o => o.value === role) ? role : (options[0]?.value ?? role)
}

function getModeRoleOptions(mode: ProcessingMode | null): Array<{ value: DriveFileWithRole['role']; label: string }> {
  switch (mode) {
    case 'bundled':
      return [
        { value: 'mbl', label: 'MBL' },
        { value: 'hbl', label: 'HBLs' }
      ]
    case 'manifest':
      return [
        { value: 'mbl', label: 'MBL' },
        { value: 'main', label: 'Manifest' }
      ]
    case 'invoices':
      return [
        { value: 'mbl', label: 'MBL' },
        { value: 'main', label: 'Invoice' }
      ]
    case 'mbl_hbl':
      return [
        { value: 'mbl', label: 'MBL' },
        { value: 'hbl', label: 'HBL' }
      ]
    default:
      return ROLE_OPTIONS
  }
}

export default function DriveGroupingPanel({
  files,
  jobGroups = [],
  mode,
  showAllTypes,
  folderName,
  isParsing = false,
  parseProgress = '',
  onToggleShowAll,
  onSetRole,
  onRemoveFile,
  onParseSelected
}: Props) {
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set())
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [fileRoles, setFileRoles] = useState<Record<string, DriveFileWithRole['role']>>({})
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null)

  const extensionFilter = useMemo(() => getExtensionFilter(mode), [mode])
  const allocatedFileIds = useMemo(
    () => new Set(jobGroups.flatMap(g => g.files.map(f => f.id))),
    [jobGroups]
  )

  const filteredFiles = useMemo(() => {
    if (showAllTypes) return files
    return files.filter(f => {
      const ext = getFileExtension(f.name)
      return extensionFilter.includes(ext)
    })
  }, [files, extensionFilter, showAllTypes])

  // Update indeterminate state for select-all checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const nonAllocatedFiles = filteredFiles.filter(f => !allocatedFileIds.has(f.id))
      const allSelected = nonAllocatedFiles.length > 0 && nonAllocatedFiles.every(f => selectedFileIds.has(f.id))
      const someSelected = nonAllocatedFiles.some(f => selectedFileIds.has(f.id)) && !allSelected
      selectAllCheckboxRef.current.checked = allSelected
      selectAllCheckboxRef.current.indeterminate = someSelected
    }
  }, [selectedFileIds, filteredFiles, allocatedFileIds])

  const handleToggleFile = (fileId: string) => {
    const newSelected = new Set(selectedFileIds)
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId)
    } else {
      newSelected.add(fileId)
    }
    setSelectedFileIds(newSelected)
  }

  const toggleGroupExpanded = (groupId: string) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId)
    } else {
      newExpanded.add(groupId)
    }
    setExpandedGroups(newExpanded)
  }

  const groupSummary = (group: DriveJobGroup): string => {
    const counts: Record<DriveFileWithRole['role'], number> = {
      mbl: 0,
      hbl: 0,
      main: 0,
      sub: 0,
      other: 0
    }
    group.files.forEach(f => counts[f.role]++)
    const parts = Object.entries(counts)
      .filter(([_, c]) => c > 0)
      .map(([role, c]) => `${c} ${role.toUpperCase()}`)
    return parts.join(' + ')
  }

  return (
    <div className="flex flex-col gap-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{folderName || 'Files'}</h2>
          <p className="text-sm text-gray-500">
            {filteredFiles.length} file{filteredFiles.length !== 1 ? 's' : ''} found
            {!showAllTypes && extensionFilter.length > 0 && ` (filtered: ${extensionFilter.join(', ')})`}
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={showAllTypes}
            onChange={onToggleShowAll}
            className="rounded border-gray-300"
          />
          Show all file types
        </label>
      </div>

      {/* File List */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b px-4 py-3 flex gap-2 items-center">
          <input
            ref={selectAllCheckboxRef}
            type="checkbox"
            onChange={() => {
              const nonAllocatedFiles = filteredFiles.filter(f => !allocatedFileIds.has(f.id))
              if (selectAllCheckboxRef.current?.checked) {
                // Select all non-allocated files
                const nonAllocatedIds = new Set(nonAllocatedFiles.map(f => f.id))
                setSelectedFileIds(nonAllocatedIds)
              } else {
                // Deselect all
                setSelectedFileIds(new Set())
              }
            }}
            className="rounded border-gray-300"
            title="Select/deselect all available files"
          />
          <div className="flex-1">File Name</div>
          <div className="w-24 text-right">Size</div>
          <div className="w-24 text-right">Modified</div>
          <div className="w-32 text-center">Role</div>
          <div className="w-20">Status</div>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {filteredFiles.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              {showAllTypes ? 'No files in folder' : 'No files match the selected filter'}
            </div>
          ) : (
            filteredFiles.map(file => {
              const isSelected = selectedFileIds.has(file.id)
              const isAllocated = allocatedFileIds.has(file.id)
              const allocatedGroup = jobGroups.find(g => g.files.some(f => f.id === file.id))
              const roleOptions = getModeRoleOptions(mode)
              const fileRole = normalizeRole(fileRoles[file.id] || autoDetectFileRole(file.name, mode), mode)

              return (
                <div
                  key={file.id}
                  className="border-b px-4 py-3 flex items-center gap-2 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleFile(file.id)}
                    disabled={isAllocated}
                    className="rounded border-gray-300 disabled:opacity-50"
                  />
                  <div className="flex-1 min-w-0">
                    <MiddleEllipsis text={file.name} className="text-sm font-medium" />
                  </div>
                  <div className="w-24 text-right text-xs text-gray-500">{formatFileSize(file.size)}</div>
                  <div className="w-24 text-right text-xs text-gray-500">{formatDate(file.modifiedTime)}</div>
                  <div className="w-32">
                    {!isAllocated ? (
                      <select
                        value={fileRole}
                        onChange={e => setFileRoles({ ...fileRoles, [file.id]: e.target.value as DriveFileWithRole['role'] })}
                        className="w-full px-2 py-1 text-xs border rounded bg-white"
                      >
                        {roleOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-gray-500">—</span>
                    )}
                  </div>
                  <div className="w-20 text-xs">
                    {isAllocated && allocatedGroup ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs whitespace-nowrap">
                        Job {allocatedGroup.id.split('-')[1]}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Parse Action */}
      {selectedFileIds.size > 0 && onParseSelected && (
        <div className="space-y-3">
          {/* Loading banner */}
          {isParsing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium text-blue-900">
                  {parseProgress || `Processing ${selectedFileIds.size} file${selectedFileIds.size !== 1 ? 's' : ''} via AI...`}
                </span>
                {!parseProgress && <span className="text-xs text-gray-600 ml-auto">(15-30 seconds)</span>}
              </div>
            </div>
          )}

          {/* Parse button */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-3">
              {selectedFileIds.size} file{selectedFileIds.size !== 1 ? 's' : ''} selected
            </p>
            <button
              onClick={() => {
                const rolesForSelected: Record<string, DriveFileWithRole['role']> = {}
                selectedFileIds.forEach(id => {
                  const raw = fileRoles[id] || autoDetectFileRole(filteredFiles.find(f => f.id === id)?.name || '', mode)
                  rolesForSelected[id] = normalizeRole(raw, mode)
                })
                onParseSelected(selectedFileIds, rolesForSelected)
              }}
              disabled={isParsing}
              className="w-full px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isParsing ? `⏳ ${parseProgress || 'Parsing files... (15-30s)'}` : 'Parse Selected Files'}
            </button>
          </div>
        </div>
      )}

      {/* Legacy Job Groups (kept for compatibility, hidden by default) */}
      {jobGroups.length > 0 && !onParseSelected && (
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900">Job Groups</h3>
          {jobGroups.map(group => {
            const isExpanded = expandedGroups.has(group.id)
            return (
              <div key={group.id} className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleGroupExpanded(group.id)}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left transition-colors"
                >
                  <div>
                    <p className="font-medium">Job {group.id.split('-')[1]}</p>
                    <p className="text-sm text-gray-600">{groupSummary(group)}</p>
                  </div>
                  <svg className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>

                {isExpanded && (
                  <div className="border-t">
                    {group.files.map((file, idx) => (
                      <div key={file.id} className={`px-4 py-3 flex items-center gap-3 ${idx > 0 ? 'border-t' : ''}`}>
                        <div className="flex-1 min-w-0">
                          <MiddleEllipsis text={file.name} className="text-sm font-medium" />
                        </div>
                        <select
                          value={file.role}
                          onChange={e => onSetRole?.(group.id, file.id, e.target.value as DriveFileWithRole['role'])}
                          className="px-2 py-1 text-sm border rounded bg-white"
                        >
                          {ROLE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => onRemoveFile?.(group.id, file.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove from group"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
